import { effect, Inject, Injectable, signal } from '@angular/core';
import type initSqlJs from "sql.js";
import { ServerAPI } from '../serverapi';
import { combineLatest, firstValueFrom, Observable, ReplaySubject, shareReplay, switchMap } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Database } from 'sql.js';


export type TranslationRepos = 'losdef' | 'losdefhelp' | 'losdefsmenu' | 'ai' | 'aitype' | 'servo'
  | 'servoparam' | 'bijzprgm' | 'bijzprgmparam' | 'afdtype' | 'losdefomschr';

@Injectable({
  providedIn: null
})
export class TranslateService {

  constructor(@Inject('ServerApi') private electronAPI: ServerAPI) {
    this.initSqlLite();
    this.loadStaticRepo('nl');
    this.currentLanguage$.subscribe(lang => this.loadStaticRepo(lang));
    effect(() => {
      let lang = this.currentLanguage();
      window.localStorage.setItem('lang', lang);
    });
    let lang = window.localStorage.getItem('lang');
    if (lang) {
      this.currentLanguage.set(lang);
    }
    // this.loadAllStaticRepos();
  }

  private async initSqlLite() {
    let sqlLite: initSqlJs.SqlJsStatic = await window.initSqlJs({ locateFile: () => "assets/sqljs/sql-wasm.wasm" });
    this.sqlLite.next(sqlLite);
  }


  public availableLanguages = ['nl', 'en'];
  public currentLanguage = signal<string>('nl');
  public currentLanguage$ = toObservable(this.currentLanguage);
  private sqlLite = new ReplaySubject<initSqlJs.SqlJsStatic>(1);
  private editing = false;

  private db = combineLatest([this.currentLanguage$, this.sqlLite]).pipe(switchMap(async ([lang, sqlLite]) => {
    let name = lang == 'nl' ? 'db/KopData.db' : 'db/KopData_en.db';
    let data = await this.electronAPI.getFile(name);
    let db = new sqlLite.Database(data);
    return db;
  }

  ), shareReplay(1));

  public kopDataDBSignal = toSignal(this.db);
  private kdataTDB = this.sqlLite.pipe(switchMap(async (sqlLite) => {
    let name = 'db/KopTrans.db';
    let data = await this.electronAPI.getFile(name);
    let db = new sqlLite.Database(data);
    return db;

  }));

  private repo: Record<string, Observable<string>> = {};

  public async searchOldKopTrans(key: string) {
    let db = await firstValueFrom(this.kdataTDB);
    let sql = `select \`index\`,txt, txt_nl, txt_en from \`Translate\` t`;
    let result = await db.exec(sql)[0].values;
    let maxdist = 100;
    let ret: { en?: string, nl?: string } = {
      'en': undefined,
      'nl': undefined
    };
    for (let r of result) {
      let txt = r[1]?.toString();
      let txten = r[3]?.toString();
      if (txt != undefined) {
        let txtp = txt.replace(/~/g, '');
        let distKey = levenshtein(key.toLowerCase(), txtp.toLowerCase());        
        let distEn = levenshtein(key.toLowerCase(), (txten || "").toLowerCase());
        if ((distKey < maxdist) || (distEn < maxdist)) {
          maxdist = Math.min(distKey, distEn);
          ret.nl = r[2]?.toString().replace(/~/g, '');
          ret.en = r[3]?.toString().replace(/~/g, '');
        }
      }
    }
    console.log(ret, maxdist);
    if (maxdist < 3) {
      if (ret.nl != undefined) {
        this.allLangStaticRepo['nl'][key] = ret.nl;
      }
      if (ret.en != undefined) {
        this.allLangStaticRepo['en'][key] = ret.en;
      }
      this.currentStaticRepo.next(this.allLangStaticRepo[this.currentLanguage()]);
    }
  }

  

  private async loadStaticRepo(lang: string) {
    if (this.editing) {
      this.currentStaticRepo.next(this.allLangStaticRepo[this.currentLanguage()]);
    } else {
      let repo = await this.electronAPI.loadStaticTranslationRepo(lang);
      this.currentStaticRepo.next(repo);  
    }
  }

  public async loadAllStaticRepos() {
    if (this.editing) {
      return;
    }
    this.editing = true;
    for (let lang of this.availableLanguages) {
      let repo = await this.electronAPI.loadStaticTranslationRepo(lang);
      this.allLangStaticRepo[lang] = repo;
    }
  }

  public async saveStaticRepo() {
    console.log("Current Repo", this.allLangStaticRepo);
    await this.electronAPI.saveStaticTranslationRepo(this.allLangStaticRepo);
  }


  public currentStaticRepo: ReplaySubject<Record<string, string>> = new ReplaySubject(1);
  public translateSignal = toSignal(this.currentStaticRepo);
  public allLangStaticRepo: Record<string, Record<string, string>> = {};
  public staticPlaces: Record<string,Set<string>> = {};

  public async getTranslation(place: string, key: string, defaultValue: string): Promise<string> {
    this.registerPlace(place, key);
    let repo = await firstValueFrom(this.currentStaticRepo);
    return repo[key] || defaultValue;
  }

  public getTranslationSync(repo: Record<string, string> | undefined, place: string, key: string, defaultValue: string, vo: Record<string, string> | undefined = undefined): string {
    this.registerPlace(place, key);
    let value = defaultValue;
    if (repo == undefined) {
      value =  defaultValue;
    } else {
      value = repo[key] || defaultValue;
    }
    return this.interpolate(value, vo);
  }

  interpolate(value: string, vo: Record<string, string> | undefined = undefined) {
    if (vo == undefined) {
      return value;
    }
    for (let key in vo) {
      value = value.replace(`{${key}}`, vo[key]);
    }
    return value;
  }

  public getTranslationForLang(lang: string, key: string): string {
    let ret =  this.allLangStaticRepo[lang][key];
    if ((ret == undefined) && (lang == 'nl')) {
      return key;
    }
    return ret || "";
  }

  public setTranslationForLang(lang: string, key: string, value: string) {
    this.allLangStaticRepo[lang][key] = value;
    this.currentStaticRepo.next(this.allLangStaticRepo[this.currentLanguage()]);
  }

  public getKeys(place: string) {
    return this.staticPlaces[place];
  }

  public registerPlace(place: string, key: string) {
    if (this.staticPlaces[place] == undefined) {
      this.staticPlaces[place] = new Set();
    }
    this.staticPlaces[place].add(key);
  }


  public translateKopDataSync(db: Database | undefined, type: TranslationRepos, key: any, defaultValue?: string): string {
    if (db == undefined) {
      return key;
    }
    if (type == 'losdef') {
        let sql = `select \`INDEX\`, PRETXT, OMSCHRIJ from \`KO-LOSDF\` where \`INDEX\` = '${key}'`;
        let ret = this.execSQL(sql, db);
        if (ret != undefined) {
          let pretxt = ret[1];
          let omschr = ret[2];
          return `${pretxt != null ? pretxt : ''}${omschr}`;
        }
        return defaultValue != undefined ? defaultValue : key;
    } else if (type == 'losdefhelp') {
      let tmp = this.oneValueSQL(`select HELPLONG from \`KO-LOSDF\` where \`INDEX\` = '${key}'`, db,
        defaultValue != undefined ? defaultValue : key);
      return tmp;

    } else if (type == 'losdefsmenu') {
      let tmp = this.oneValueSQL(`select OMSCHR from \`KO-SMENU\` where \`INDEX\` = '${key}'`, db,
        defaultValue != undefined ? defaultValue : key);
      return tmp;

    } else if (type == 'ai') {
      let tmp = this.oneValueSQL(`select OMSCHRIJ from \`KO-ANA\` where \`INDEX\` = '${key}'`, db,
        defaultValue != undefined ? defaultValue : key);
      return tmp;
    } else if (type == 'aitype') {
      let tmp = this.oneValueSQL(`select OMSCHRIJ from \`KO-ANAT\` where \`INDEX\` = '${key}'`, db,
        defaultValue != undefined ? defaultValue : key);
      return tmp;
    } else if (type == 'servo') {
      let tmp = this.oneValueSQL(`select OMSCHR from \`KO-SERVO\` where \`INDEX\` = '${key}'`,db,
        defaultValue != undefined ? defaultValue : key);
      return tmp;
    } else if (type == 'servoparam') {
      let parts = key.split('.');
      let servo = parts[0];
      let param = parts[1];
      let sql = `select OMPARAM${param} from \`KO-SERVO\` where \`INDEX\` = '${servo}'`;
      let tmp = this.oneValueSQL(sql, db,
        defaultValue != undefined ? defaultValue : key);
      return tmp;
    } else if (type == 'bijzprgm') {
      let tmp = this.oneValueSQL(`select OMSCHRIJ from \`KO-BIJZ\` where \`INDEX\` = '${key}'`, db,
        defaultValue != undefined ? defaultValue : key);
      return tmp;
    } else if (type == 'bijzprgmparam') {
      let parts = key.split('.');
      let servo = parts[0];
      let param = parts[1];
      let sql = `select OMPARAM${param} from \`KO-BIJZ\` where \`INDEX\` = '${servo}'`;
      let ret = this.oneValueSQL(sql, db, defaultValue || "");
      return ret;
    } else if (type == 'afdtype') {
      let ret = this.oneValueSQL(`select NAAM from \`KO-AFDEL\` where \`TYPE\` = '${key}'`, db, defaultValue || "");
      return ret;
    } else if (type == 'losdefomschr') {
      let tmp = this.oneValueSQL(`select OMSCHRIJPR from \`KO-LOSDF\` where \`INDEX\` = '${key}'`, db,
        defaultValue != undefined ? defaultValue : key);
      return tmp;
    }
    return defaultValue != undefined ? defaultValue : key;
  }
  public translateKopData(type: TranslationRepos, key: any, defaultValue?: string): Observable<string> {

    let rkey = `${type}|${key}`;
    if (this.repo[rkey] != undefined) {
      return this.repo[rkey];
    }
    if (type == 'losdef') {
      let tmp = this.db.pipe(switchMap(async (db) => {
        let sql = `select \`INDEX\`, PRETXT, OMSCHRIJ from \`KO-LOSDF\` where \`INDEX\` = '${key}'`;
        let ret = this.execSQL(sql, db);
        if (ret != undefined) {
          let pretxt = ret[1];
          let omschr = ret[2];
          return `${pretxt != null ? pretxt : ''}${omschr}`;
        }
        return defaultValue != undefined ? defaultValue : key;

      }), shareReplay(1));
      this.repo[rkey] = tmp;
      return tmp;

    } else if (type == 'losdefhelp') {
      let tmp = this.makeOneCelKopDataObservable(`select HELPLONG from \`KO-LOSDF\` where \`INDEX\` = '${key}'`,
        defaultValue != undefined ? defaultValue : key);
      this.repo[rkey] = tmp;
      return tmp;

    } else if (type == 'losdefsmenu') {
      let tmp = this.makeOneCelKopDataObservable(`select OMSCHR from \`KO-SMENU\` where \`INDEX\` = '${key}'`,
        defaultValue != undefined ? defaultValue : key);
      this.repo[rkey] = tmp;
      return tmp;

    } else if (type == 'ai') {
      let tmp = this.makeOneCelKopDataObservable(`select OMSCHRIJ from \`KO-ANA\` where \`INDEX\` = '${key}'`,
        defaultValue != undefined ? defaultValue : key);
      this.repo[rkey] = tmp;
      return tmp;
    } else if (type == 'aitype') {
      let tmp = this.makeOneCelKopDataObservable(`select OMSCHRIJ from \`KO-ANAT\` where \`INDEX\` = '${key}'`,
        defaultValue != undefined ? defaultValue : key);
      this.repo[rkey] = tmp;
      return tmp;
    } else if (type == 'servo') {
      let tmp = this.makeOneCelKopDataObservable(`select OMSCHR from \`KO-SERVO\` where \`INDEX\` = '${key}'`,
        defaultValue != undefined ? defaultValue : key);
      this.repo[rkey] = tmp;
      return tmp;
    } else if (type == 'servoparam') {
      let parts = key.split('.');
      let servo = parts[0];
      let param = parts[1];
      let sql = `select OMPARAM${param} from \`KO-SERVO\` where \`INDEX\` = '${servo}'`;
      let tmp = this.makeOneCelKopDataObservable(sql,
        defaultValue != undefined ? defaultValue : key);
      this.repo[rkey] = tmp;
      return tmp;
    } else if (type == 'bijzprgm') {
      let tmp = this.makeOneCelKopDataObservable(`select OMSCHRIJ from \`KO-BIJZ\` where \`INDEX\` = '${key}'`,
        defaultValue != undefined ? defaultValue : key);
      this.repo[rkey] = tmp;
      return tmp;
    } else if (type == 'bijzprgmparam') {
      let parts = key.split('.');
      let servo = parts[0];
      let param = parts[1];
      let sql = `select OMPARAM${param} from \`KO-BIJZ\` where \`INDEX\` = '${servo}'`;
      let tmp = this.makeOneCelKopDataObservable(sql,
        defaultValue != undefined ? defaultValue : key);
      this.repo[rkey] = tmp;
      return tmp;
    } else if (type == 'afdtype') {
      let tmp = this.makeOneCelKopDataObservable(`select NAAM from \`KO-AFDEL\` where \`TYPE\` = '${key}'`,
        defaultValue != undefined ? defaultValue : key);
      this.repo[rkey] = tmp;
      return tmp;
    } else if (type == 'losdefomschr') {
      let tmp = this.makeOneCelKopDataObservable(`select OMSCHRIJPR from \`KO-LOSDF\` where \`INDEX\` = '${key}'`,
        defaultValue != undefined ? defaultValue : key);
      this.repo[rkey] = tmp;
      return tmp;
    }
    return this.db.pipe(switchMap(async (db) => {
      return defaultValue != undefined ? defaultValue : key;
    }), shareReplay(1));

  }


  

  private execSQL(sql: string, db: initSqlJs.Database) {
    let ret = db.exec(sql);
    if (ret.length > 0) {
      let res = ret[0];
      if (res.values.length > 0) {
        return res.values[0];
      }
    }
    return undefined;
  }

  private oneValueSQL(sql: string, db: initSqlJs.Database, defaultValue: string) {
    let ret = db.exec(sql);
    if (ret.length > 0) {
      let res = ret[0];
      if (res.values.length > 0) {
        let ret =  res.values[0];
        return `${ret != null ? ret : ''}`;
      }
    }
    return defaultValue;
  }
  private makeOneCelKopDataObservable(sql: string, defaultValue: string): Observable<string> {
    let ret = this.db.pipe(switchMap(async (db) => {
      let ret = this.execSQL(sql, db);
      if (ret != undefined) {
        let helplong = ret[0];
        return `${helplong != null ? helplong : ''}`;
      }
      return defaultValue;

    }), shareReplay(1));
    return ret;
  }

}


export function levenshtein(a: string, b: string): number
{
	const an = a ? a.length : 0;
	const bn = b ? b.length : 0;
	if (an === 0)
	{
		return bn;
	}
	if (bn === 0)
	{
		return an;
	}
	const matrix = new Array<number[]>(bn + 1);
	for (let i = 0; i <= bn; ++i)
	{
		let row = matrix[i] = new Array<number>(an + 1);
		row[0] = i;
	}
	const firstRow = matrix[0];
	for (let j = 1; j <= an; ++j)
	{
		firstRow[j] = j;
	}
	for (let i = 1; i <= bn; ++i)
	{
		for (let j = 1; j <= an; ++j)
		{
			if (b.charAt(i - 1) === a.charAt(j - 1))
			{
				matrix[i][j] = matrix[i - 1][j - 1];
			}
			else
			{
				matrix[i][j] = Math.min(
					matrix[i - 1][j - 1], // substitution
					matrix[i][j - 1], // insertion
					matrix[i - 1][j] // deletion
				) + 1;
			}
		}
	}
	return matrix[bn][an];
};